import { CreditCardDetails } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/payment_method_pb";
import { ZuoraMigrationStatus } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import {
  Period,
  Subscription,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { CardExpirationStatus } from "constants/payment-method";

// Determines whether the card is EXPIRED, EXPIRING or ACTIVE.
export const getCardExpirationStatus = (
  creditCard: CreditCardDetails
): CardExpirationStatus => {
  const expirationMonth = creditCard.expirationMonth;
  const expirationYear = creditCard.expirationYear;
  const now = new Date();
  const currentYear = now.getFullYear();
  // getMonth() returns 0-indexed months. i.e. January is 0, February is 1, etc.
  const currentMonth = now.getMonth() + 1;

  if (
    currentYear > expirationYear ||
    (currentYear === expirationYear && currentMonth > expirationMonth)
  ) {
    return CardExpirationStatus.EXPIRED;
  }

  if (currentYear === expirationYear && currentMonth === expirationMonth) {
    return CardExpirationStatus.EXPIRING;
  }

  return CardExpirationStatus.ACTIVE;
};

export const isFreeTeam = (subscription: Subscription | undefined): boolean => {
  if (!subscription) {
    return true;
  }
  return subscription?.type === SubscriptionType.FREE_ORG;
};

export const getBillingFrequency = (
  subscription: Subscription | undefined
): Period => {
  const isFree = isFreeTeam(subscription);
  if (!subscription || isFree) {
    return Period.MONTH;
  }
  return SubscriptionTypeBillingPeriodKeyMap[subscription.type];
};

export const getSubscriptionCurrency = (
  subscription: Subscription | undefined
) => {
  const isFree = isFreeTeam(subscription);
  if (!subscription || isFree) {
    return undefined;
  }

  return subscription.chargify?.currency?.toUpperCase();
};

export type BillingPeriod =
  | "year"
  | "month"
  | "half-year"
  | "quarter"
  | "two-year"
  | "three-year";

// The billing period key from `pricing -> period` in subscription JSON
export enum BillingPeriodKey {
  Month = "month",
  Quarter = "quarter",
  HalfYear = "half-year",
  Year = "year",
  TwoYear = "two-year",
  ThreeYear = "three-year",
}

export const SubscriptionTypeBillingPeriodKeyMap: Record<
  SubscriptionType,
  Period
> = {
  [SubscriptionType.FREE_ORG]: Period.MONTH,
  [SubscriptionType.FREE]: Period.MONTH,
  [SubscriptionType.PREMIUM_MONTHLY]: Period.MONTH,
  [SubscriptionType.PREMIUM_ANNUAL]: Period.YEAR,
  [SubscriptionType.PREMIUM_QUARTERLY]: Period.QUARTER,
  [SubscriptionType.PREMIUM_BIANNUAL]: Period.HALF_YEAR,
  [SubscriptionType.PREMIUM_TWO_YEAR]: Period.TWO_YEAR,
  [SubscriptionType.PREMIUM_THREE_YEAR]: Period.THREE_YEAR,
};

export const isUnderZuoraMigration = (
  status: ZuoraMigrationStatus | undefined
): boolean => {
  return (
    status !== undefined &&
    status !== ZuoraMigrationStatus.UNSPECIFIED &&
    status !== ZuoraMigrationStatus.COMPLETED_BILLING_OPS_RESUMED
  );
};

export const getBillingProviderName = (
  subscription: Subscription | undefined
): string => {
  switch (subscription?.billingProvider) {
    case "ZUORA":
      return "Zuora";
    default:
      return "Chargify";
  }
};

export const getBillingProviderSite = (
  subscription: Subscription | undefined
): string => {
  switch (subscription?.billingProvider) {
    case "ZUORA":
      return "";
    default:
      return subscription?.chargify?.site || "";
  }
};

export const getCouponProvider = (
  subscription: Subscription | undefined
): string => {
  switch (subscription?.billingProvider) {
    case "ZUORA":
      return "zuora";
    default:
      return subscription?.chargify?.site || "";
  }
};
